import { LegalLayout } from '../../../components/layout/LegalLayout';
import {
  LEGAL,
  formatHeadquarters,
  formatSiren,
  formatSiret,
  legalValue,
} from '@shared/constants/legal-info';
import { SITE, absoluteUrl } from '@shared/constants/site';

export default function MentionsLegales() {
  return (
    <LegalLayout
      title="Mentions légales"
      url={absoluteUrl('/legal/mentions-legales')}
      lastUpdated="01/05/2026"
    >
      <p>
        Les présentes mentions légales sont publiées en application de l'article 6, III de la loi
        n° 2004-575 du 21 juin 2004 pour la confiance dans l'économie numérique (LCEN). Elles
        s'appliquent à l'ensemble du site <strong>{SITE.url.replace(/^https?:\/\//, '')}</strong> et
        à ses sous-domaines.
      </p>

      <h2>1. Éditeur du site</h2>
      <p>
        <strong>{LEGAL.legalName}</strong> — Société par Actions Simplifiée ({LEGAL.legalForm}) au
        capital social de{' '}
        <em>{legalValue(LEGAL.capitalEur, 'capital en euros')}</em>
        {LEGAL.capitalEur !== null ? ' €' : ''}.
      </p>
      <ul>
        <li>
          <strong>Siège social :</strong> <em>{formatHeadquarters()}</em>
        </li>
        <li>
          <strong>SIREN :</strong> {formatSiren(LEGAL.siren)}
        </li>
        <li>
          <strong>SIRET (siège) :</strong> <em>{formatSiret(LEGAL.siretSiege)}</em>
        </li>
        <li>
          <strong>RCS :</strong> <em>{legalValue(LEGAL.rcsCity, 'ville d\'immatriculation')}</em>
        </li>
        <li>
          <strong>Code APE / NAF :</strong>{' '}
          <em>{legalValue(LEGAL.apeCode, 'ex. 7022Z conseil ou 4321A installation électrique')}</em>
        </li>
        <li>
          <strong>N° TVA intracommunautaire :</strong>{' '}
          <em>{legalValue(LEGAL.vatNumber, 'FR + 11 chiffres')}</em>
        </li>
        {LEGAL.phone && (
          <li>
            <strong>Téléphone :</strong> <em>{LEGAL.phone}</em>
          </li>
        )}
        <li>
          <strong>Courriel :</strong>{' '}
          <a href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>
        </li>
      </ul>

      <h2>2. Direction de la publication</h2>
      <p>
        Le directeur de la publication, au sens de l'article 93-2 de la loi du 29 juillet 1982
        modifiée, est{' '}
        <em>{legalValue(LEGAL.publicationDirector, 'Prénom Nom du Président de la SAS')}</em>, en sa
        qualité de représentant légal.
      </p>

      <h2>3. Hébergement</h2>
      <p>Le site est hébergé sur une infrastructure située en Union européenne :</p>
      <ul>
        <li>
          <strong>Application & rendu :</strong> serveurs hébergés en France (région UE).
        </li>
        <li>
          <strong>Distribution & WAF :</strong> Cloudflare, Inc. — 101 Townsend Street, San
          Francisco, CA 94107, USA. Zones de cache restreintes à l'UE pour les contenus dynamiques.
        </li>
        <li>
          <strong>Stockage objet (médias, documents) :</strong> Cloudflare R2, région{' '}
          <strong>EU (eur)</strong>.
        </li>
        <li>
          <strong>Base de données :</strong> PostgreSQL hébergé en Union européenne (région{' '}
          <em>eu-west</em>).
        </li>
      </ul>

      <h2>4. Activités exercées et statut juridique</h2>
      <p>
        Solution Éco France est un <strong>acteur intégré</strong> de la transition énergétique,
        opérant sur trois pôles distincts dont les modèles économiques et statuts juridiques sont
        précisés dans nos{' '}
        <a href="/legal/cgv-courtier-energie">CGV et mandats</a> :
      </p>
      <ul>
        <li>
          <strong>Pôle Travaux & Rénovation énergétique</strong> — coordination d'un réseau
          d'artisans RGE partenaires (Qualibat, QualiPAC, Qualibois, Qualifelec).
        </li>
        <li>
          <strong>Pôle Courtage énergie B2B</strong> — courtier{' '}
          <strong>indépendant des fournisseurs d'énergie</strong>
          {LEGAL.oriasNumber && (
            <>
              , immatriculé au registre des intermédiaires (référence ORIAS{' '}
              <em>n° {LEGAL.oriasNumber}</em>)
            </>
          )}
          .
        </li>
        <li>
          <strong>Pôle Mandataire CEE</strong> — mandataire au sens de l'article R. 221-3 du Code
          de l'énergie, en partenariats commerciaux assumés avec plusieurs obligés et délégataires
          référencés au PNCEE.
        </li>
      </ul>

      <h2>5. Médiation et règlement des litiges</h2>
      <p>
        Conformément aux articles L. 612-1 et suivants du Code de la consommation, le client
        consommateur a la possibilité de recourir gratuitement à un médiateur :
      </p>
      <ul>
        <li>
          <strong>Pôle Courtage énergie :</strong> Médiateur national de l'énergie — Libre réponse
          n° 59252, 75443 Paris Cedex 09 —{' '}
          <a href="https://www.energie-mediateur.fr" rel="noopener noreferrer" target="_blank">
            energie-mediateur.fr
          </a>
          .
        </li>
        <li>
          <strong>Pôles Travaux et CEE :</strong>{' '}
          <em>
            {legalValue(
              LEGAL.consumerMediator,
              'organisme adhéré, ex. CM2C ou Médiateur du e-commerce de la FEVAD',
            )}
          </em>
          .
        </li>
        <li>
          <strong>Plateforme européenne de règlement en ligne des litiges :</strong>{' '}
          <a href="https://ec.europa.eu/consumers/odr" rel="noopener noreferrer" target="_blank">
            ec.europa.eu/consumers/odr
          </a>
          .
        </li>
      </ul>

      <h2>6. Propriété intellectuelle</h2>
      <p>
        L'ensemble du site (architecture, textes, images, vidéos, logo, marque{' '}
        <em>Solution Éco France</em>, base de données, code source, identité visuelle) est la
        propriété exclusive de Solution Éco France ou de ses partenaires titulaires des droits.
      </p>
      <p>
        Toute reproduction, représentation, modification, publication, transmission, dénaturation,
        totale ou partielle, par quelque procédé que ce soit, est interdite sans autorisation
        écrite préalable, sous peine de poursuites civiles et pénales (articles L. 335-2 et
        suivants du Code de la propriété intellectuelle).
      </p>

      <h2>7. Crédits</h2>
      <ul>
        <li>
          <strong>Conception & développement :</strong> équipe Solution Éco France.
        </li>
        <li>
          <strong>Design system :</strong> identité visuelle propriétaire — typographies Inter
          (Rasmus Andersson, OFL), Inter Tight (OFL), Geist (Vercel, OFL).
        </li>
        <li>
          <strong>Iconographie :</strong> Lucide (ISC License).
        </li>
        <li>
          <strong>Photographies :</strong> images sous licence ou publiées avec autorisation écrite
          des personnes représentées (clients, artisans).
        </li>
        <li>
          <strong>Données publiques :</strong> Sirene v3 (INSEE, Licence Ouverte 2.0), ADEME,
          PNCEE, EMMY (registre national des CEE).
        </li>
      </ul>

      <h2>8. Loi applicable et juridiction</h2>
      <p>
        Les présentes mentions légales sont régies par le droit français. Tout litige relatif à
        leur interprétation ou à leur exécution relève, à défaut de résolution amiable, des
        juridictions du ressort du siège social de l'éditeur.
      </p>

      <h2>9. Signalement de contenus illicites</h2>
      <p>
        Conformément à l'article 6, I-5 de la LCEN, tout contenu jugé illicite peut être signalé
        à : <a href={`mailto:${LEGAL.abuseEmail}`}>{LEGAL.abuseEmail}</a>. Nous nous engageons à
        traiter chaque signalement dans les meilleurs délais.
      </p>
    </LegalLayout>
  );
}
